import React, { FC, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ResponsiveLine } from '@nivo/line';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import Explainer, { ExplainerSection } from '../../components/Explainer';
import { tokens } from '../../theme';
import { useChartTheme } from '../../themes/chartTheme';
import { formatNumber } from '../../helpers/numberUtils';
import {
  fetchKqStreitfaelle,
  fetchKuendigungsquoten,
  fetchPlxProducts,
  KqCohort,
  KqMode,
  KqResponse,
  KqStreitfaelleResponse,
  PlxProduct,
} from '../../helpers/plxOrdersData';

const COLOR_KQ1 = '#4cceac';
const COLOR_KQ2_BESTELLER = '#6870fa';
const COLOR_KQ2_ZAHLER = '#ffb74d';

function buildExplainerSections(mode: KqMode, wkzWithoutHistory: number): ExplainerSection[] {
  return [
    {
      label: 'Datengrundlage',
      body: 'Alle Quartalsabo-Anfos (Bestellungen ohne Verweigerte, ohne Einmalverkäufe und Jahres-Upsells), gruppiert nach Bestellmonat. Kündigungsquote 1 = Anteil ohne Meilenstein „1. Rechnung", Kündigungsquote 2 analog für die 2. Rechnung — mit zwei Basen (alle Besteller = Prio 1, Zahler der 1. Rechnung). Eine Kohorte wird erst gewertet, wenn alle ihre Rechnungen fällig waren plus 14 Tage Verbuchungspuffer; der Rolling-Wert poolt die jüngsten voll gereiften Kohorten.',
    },
    {
      label: 'Zwei Berechnungsarten',
      body: `Reale Rechnungen sind nicht immer der Katalogpreis — es gibt anteilige Schlussrechnungen (z. B. 86,40 € bei einem 374,70-€-Produkt, wenn mitten in der Periode gekündigt wird), und die Preis-Historie der Kampagnen wird erst seit November 2025 gepflegt. Deshalb zwei Sichten: BETRAG (streng) wertet eine Rechnung nur als bezahlt, wenn die kumulierte Zahlung den vollen Katalogpreis deckt — dafür muss der damalige Preis bekannt sein, ältere Kohorten bleiben ohne Wert (aktuell ${formatNumber(wkzWithoutHistory, 0)} Kampagnen-Codes ohne gepflegte Preis-Historie; werden die alten WKZ-Configs nachgepflegt, füllt sich diese Sicht rückwirkend). LAUFZEIT wertet stattdessen, ob das Abo bei Fälligkeit der Rechnung noch lief und überhaupt gezahlt wurde — braucht keinen Preis und deckt alle Kohorten, misst aber Verbleib statt Zahlungshöhe (Mahn-Verzug kann einzelne Fälle zu günstig werten).`,
    },
    {
      label: 'Anteilige Rechnungen (nur Laufzeit-Modus)',
      body: 'Standard („zählen nicht"): eine Rechnung gilt erst als bezahlt, wenn die volle Periode konsumiert wurde — wer mitten in der Periode kündigt und nur die anteilige Schlussrechnung zahlt, zählt nicht als Zahler dieser Rechnung; wo der Preis bekannt ist, entscheidet der Betrag. Mit „zählen": jede angebrochene, bezahlte Periode zählt.',
    },
    {
      label: 'Wichtig',
      highlight: true,
      body:
        mode === 'amount'
          ? 'Die jüngste belastbare Kohorte liegt strukturell 2–3 Monate zurück (Testphase 30 Tage + Verbuchung). Im Betrag-Modus sind Kohorten vor Dezember 2025 großteils „nicht bewertbar" (fehlende Preis-Historie) — der Laufzeit-Modus zeigt sie. Späte Zahlungen und Rückbuchungen können Werte nachträglich leicht verschieben. Unsichere Fälle listet das Streitfälle-Widget unten.'
          : 'Der Laufzeit-Modus misst „gezahlt und geblieben", nicht die Zahlungshöhe — eine rechnungsgenaue Zahlungs-Klassifikation gibt es erst mit Einzel-Zahlungsdaten von PLX. Die jüngste belastbare Kohorte liegt strukturell 2–3 Monate zurück (im strengen Modus für Kü2 bis ~7 Monate, weil die volle Periode als Beleg dient). Unsichere Fälle listet das Streitfälle-Widget unten.',
    },
  ];
}

const FLAG_LABELS: Record<string, string> = {
  preis_unbekannt: 'Preis unbekannt',
  ohne_cap: 'Ohne Abo-Ende/Kündigung',
  anteilig_verdacht: 'Anteilig-Verdacht',
  betrag_vs_laufzeit: 'Betrag ≠ Laufzeit',
  zahlt_ohne_faktura: 'Zahlt ohne Faktura',
  zahlung_ueber_faktura: 'Zahlung über Faktura',
};

const fmtPaid = (p: [number, number] | null): string => (p === null ? '—' : `${p[0]}/${p[1]}`);

/**
 * `eingebettet`: ohne eigenen Seitenkopf und Außenrand — so zeigt die Sektion /auswertungen die Seite als
 * Unterseite (Till 08.10.2026: einbetten, Nachschliff in P5). Ohne den Schalter unverändert.
 */
const KuendigungsquotenPage: FC<{ eingebettet?: boolean }> = ({ eingebettet = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<KqResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [excludeTestOrders, setExcludeTestOrders] = useState(true);
  const [selectedArtikel, setSelectedArtikel] = useState<string[]>([]);
  const [windowSize, setWindowSize] = useState(3);
  const [products, setProducts] = useState<PlxProduct[]>([]);
  const [mode, setMode] = useState<KqMode>(() =>
    searchParams.get('mode') === 'date' ? 'date' : 'amount',
  );
  const [countPartial, setCountPartial] = useState<boolean>(
    () => searchParams.get('partial') === '1',
  );

  // Streitfälle (lazy beim Aufklappen)
  const [streitOpen, setStreitOpen] = useState(false);
  const [streit, setStreit] = useState<KqStreitfaelleResponse | null>(null);
  const [streitPage, setStreitPage] = useState(1);
  const [streitLoading, setStreitLoading] = useState(false);

  const syncUrl = (nextMode: KqMode, nextPartial: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (nextMode === 'date') next.set('mode', 'date');
    else next.delete('mode');
    if (nextMode === 'date' && nextPartial) next.set('partial', '1');
    else next.delete('partial');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchPlxProducts();
        if (!cancelled) setProducts(list.filter((p) => !p.is_one_time));
      } catch (e) {
        console.error('Error loading products:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchKuendigungsquoten(
          selectedArtikel.length > 0 ? selectedArtikel : undefined,
          excludeTestOrders,
          windowSize,
          mode,
          mode === 'date' ? countPartial : undefined,
        );
        if (!cancelled) setData(result);
      } catch (e) {
        console.error('Error loading Kündigungsquoten:', e);
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [excludeTestOrders, selectedArtikel, windowSize, mode, countPartial]);

  useEffect(() => {
    if (!streitOpen) return;
    let cancelled = false;
    (async () => {
      setStreitLoading(true);
      try {
        const result = await fetchKqStreitfaelle(
          selectedArtikel.length > 0 ? selectedArtikel : undefined,
          excludeTestOrders,
          streitPage,
          50,
        );
        if (!cancelled) setStreit(result);
      } catch (e) {
        console.error('Error loading Streitfälle:', e);
      } finally {
        if (!cancelled) setStreitLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [streitOpen, streitPage, selectedArtikel, excludeTestOrders]);

  if (loading) return <LoadingSpinner />;

  const rolling = data?.rolling;
  const cohorts = data?.cohorts ?? [];
  const isDate = mode === 'date';

  const fmtRate = (v: number | null): string => (v === null ? '—' : `${formatNumber(v, 1)} %`);
  const fmtDelta = (v: number | null): string =>
    v === null ? '' : `${v > 0 ? '+' : ''}${formatNumber(v, 1)} pp vs. Vormonat`;
  const monthsRange = (months: string[]): string =>
    months.length === 0
      ? 'keine gereifte Kohorte'
      : `Kohorten ${months[0]} – ${months[months.length - 1]}`;

  // niedriger = besser: Verschlechterung (delta > 0) rot einfärben
  const deltaColor = (v: number | null): string =>
    v === null ? colors.grey[300] : v > 0 ? '#e57373' : '#4cceac';

  const modeHint = isDate ? (countPartial ? 'Laufzeit, anteilige zählen' : 'Laufzeit') : 'Betrag';

  const kpiCards = rolling
    ? [
        {
          title: `Kündigungsquote 1 (Rolling ${rolling.window}M · ${modeHint})`,
          value: fmtRate(rolling.kq1),
          delta: rolling.kq1_delta,
          subtitle: `Meilenstein „1. Rechnung" verfehlt · ${monthsRange(rolling.kq1_months)}`,
          color: COLOR_KQ1,
        },
        {
          title: `Kündigungsquote 2 — Basis Besteller (Rolling ${rolling.window}M · ${modeHint})`,
          value: fmtRate(rolling.kq2_besteller),
          delta: rolling.kq2_besteller_delta,
          subtitle: `Meilenstein „2. Rechnung" verfehlt, bezogen auf alle Besteller · ${monthsRange(rolling.kq2_months)}`,
          color: COLOR_KQ2_BESTELLER,
        },
        {
          title: `Kündigungsquote 2 — Basis Zahler (Rolling ${rolling.window}M · ${modeHint})`,
          value: fmtRate(rolling.kq2_zahler),
          delta: rolling.kq2_zahler_delta,
          subtitle: `Meilenstein „2. Rechnung" verfehlt, bezogen auf Zahler der 1. Rechnung · ${monthsRange(rolling.kq2_months)}`,
          color: COLOR_KQ2_ZAHLER,
        },
      ]
    : [];

  // Chart: nur gereifte Punkte (null-Punkte weglassen — Nivo verträgt keine nulls) und nur
  // Kohorten mit genug bewertbaren Orders — Mini-Kohorten erzeugen sonst 0/100-%-Zacken.
  const MIN_CHART_N = 20;
  const chartCohorts = cohorts.filter((c) => c.anfos_known_price >= MIN_CHART_N);
  const chartSeries = [
    {
      id: 'Kü1',
      data: chartCohorts
        .filter((c) => c.kq1 !== null)
        .map((c) => ({ x: c.month, y: Number(c.kq1!.toFixed(2)) })),
    },
    {
      id: 'Kü2 (Besteller)',
      data: chartCohorts
        .filter((c) => c.kq2_besteller !== null)
        .map((c) => ({ x: c.month, y: Number(c.kq2_besteller!.toFixed(2)) })),
    },
    {
      id: 'Kü2 (Zahler)',
      data: chartCohorts
        .filter((c) => c.kq2_zahler !== null)
        .map((c) => ({ x: c.month, y: Number(c.kq2_zahler!.toFixed(2)) })),
    },
  ].filter((s) => s.data.length > 0);

  const tableRows = [...cohorts].reverse();
  const tableHeaders = [
    'Bestellmonat',
    'Anfos',
    'nicht bewertbar',
    ...(isDate ? ['davon per Laufzeit'] : []),
    'Rechnung 1 bezahlt',
    'Rechnung 2 bezahlt',
    'Kü1',
    'Kü2 (Besteller)',
    'Kü2 (Zahler)',
  ];

  const flagChips = streit
    ? Object.entries(FLAG_LABELS)
        .map(([key, label]) => ({
          key,
          label,
          count: (streit.totals as any)[key] as number,
        }))
        .filter((f) => f.count > 0)
    : [];

  return (
    <Box m={eingebettet ? 0 : '20px'}>
      {!eingebettet && (
        <Header
          title="Kündigungsquoten"
          subtitle="Quartalsabos: Nicht-Zahler der 1. und 2. Rechnung je Bestellmonats-Kohorte"
        />
      )}

      <Explainer
        title="Wie diese Auswertung funktioniert"
        sections={buildExplainerSections(mode, data?.meta.wkz_without_history ?? 0)}
      />

      {/* Filters */}
      <Box sx={{ my: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v) => {
            if (v !== null) {
              setMode(v);
              syncUrl(v, countPartial);
            }
          }}
        >
          <ToggleButton value="amount">Betrag (streng)</ToggleButton>
          <ToggleButton value="date">Laufzeit</ToggleButton>
        </ToggleButtonGroup>
        {isDate && (
          <FormControlLabel
            control={
              <Checkbox
                checked={countPartial}
                onChange={(e) => {
                  setCountPartial(e.target.checked);
                  syncUrl(mode, e.target.checked);
                }}
                sx={{ color: colors.greenAccent[200] }}
              />
            }
            label="Anteilige Rechnungen zählen"
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={excludeTestOrders}
              onChange={(e) => setExcludeTestOrders(e.target.checked)}
              sx={{ color: colors.greenAccent[200] }}
            />
          }
          label="Testbestellungen (PLZ 53119) ausschließen"
        />
        <Autocomplete
          multiple
          options={products}
          getOptionLabel={(option) => `${option.plx_product} — ${option.name}`}
          value={products.filter((p) => selectedArtikel.includes(p.plx_product))}
          onChange={(_, newValue) => setSelectedArtikel(newValue.map((v) => v.plx_product))}
          renderInput={(params) => (
            <TextField {...params} label="Produkt-Filter" placeholder="Produkte auswählen..." />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option.plx_product}
                  {...chipProps}
                  sx={{ backgroundColor: colors.blueAccent[700] }}
                />
              );
            })
          }
          sx={{ minWidth: 300 }}
        />
        <ToggleButtonGroup
          value={windowSize}
          exclusive
          size="small"
          onChange={(_, v) => {
            if (v !== null) setWindowSize(v);
          }}
        >
          <ToggleButton value={3}>Rolling 3M</ToggleButton>
          <ToggleButton value={6}>Rolling 6M</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Typography color="#e57373" sx={{ mb: 2 }}>
          Fehler beim Laden: {error}
        </Typography>
      )}

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color={colors.grey[100]} gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="h3" sx={{ color: card.color, fontWeight: 'bold', mb: 0.5 }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" sx={{ color: deltaColor(card.delta), mb: 0.5 }}>
                  {fmtDelta(card.delta)}
                </Typography>
                <Typography variant="caption" color={colors.grey[300]}>
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Chart */}
      {chartSeries.length > 0 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" mb={2}>
              Verlauf je Bestellmonats-Kohorte ({modeHint})
            </Typography>
            <Box height="420px">
              <ResponsiveLine
                data={chartSeries as any}
                theme={chartTheme}
                margin={{ top: 20, right: 170, bottom: 55, left: 60 }}
                xScale={{ type: 'point' }}
                yScale={{ type: 'linear', min: 0, max: 100, stacked: false }}
                curve="monotoneX"
                colors={({ id }: any) =>
                  id === 'Kü1'
                    ? COLOR_KQ1
                    : id === 'Kü2 (Besteller)'
                      ? COLOR_KQ2_BESTELLER
                      : COLOR_KQ2_ZAHLER
                }
                lineWidth={2.5}
                enablePoints
                pointSize={6}
                enableGridX={false}
                enableGridY
                axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: -30 }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  legend: 'Kündigungsquote (%)',
                  legendPosition: 'middle',
                  legendOffset: -45,
                }}
                enableSlices="x"
                legends={[
                  {
                    anchor: 'bottom-right',
                    direction: 'column',
                    translateX: 150,
                    itemWidth: 140,
                    itemHeight: 20,
                    symbolSize: 12,
                    symbolShape: 'circle',
                  },
                ]}
              />
            </Box>
            <Typography variant="caption" color={colors.grey[300]} sx={{ display: 'block', mt: 1 }}>
              Eine Kohorte erscheint erst, wenn alle ihre Rechnungen fällig und verbucht sind: Kü1
              ~1,5 Monate nach Bestellmonat (Testphase 30 T. + Verbuchung), Kü2 ~4,5 Monate (+ 90 T.
              bis zur 2. Rechnung; im Laufzeit-Modus ohne anteilige Rechnungen bis ~7 Monate) — die
              Kü2-Kurven enden deshalb früher als Kü1.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Cohort table */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Kohorten-Detail ({modeHint})
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {tableHeaders.map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'right',
                        padding: '8px 12px',
                        borderBottom: `2px solid ${colors.grey[700]}`,
                        color: colors.grey[100],
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((c: KqCohort) => (
                  <tr key={c.month}>
                    <td style={{ textAlign: 'right', padding: '6px 12px' }}>{c.month}</td>
                    <td style={{ textAlign: 'right', padding: '6px 12px' }}>
                      {formatNumber(c.anfos_total, 0)}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 12px' }}>
                      {c.unknown_price > 0 ? formatNumber(c.unknown_price, 0) : '–'}
                    </td>
                    {isDate && (
                      <td style={{ textAlign: 'right', padding: '6px 12px' }}>
                        {c.derived_lifetime_count > 0
                          ? formatNumber(c.derived_lifetime_count, 0)
                          : '–'}
                      </td>
                    )}
                    <td style={{ textAlign: 'right', padding: '6px 12px' }}>
                      {c.anfos_known_price === 0
                        ? '–'
                        : c.mature_1
                          ? formatNumber(c.paid_1, 0)
                          : 'unreif'}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 12px' }}>
                      {c.anfos_known_price === 0
                        ? '–'
                        : c.mature_2
                          ? formatNumber(c.paid_2, 0)
                          : 'unreif'}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 12px', color: COLOR_KQ1 }}>
                      {fmtRate(c.kq1)}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        padding: '6px 12px',
                        color: COLOR_KQ2_BESTELLER,
                      }}
                    >
                      {fmtRate(c.kq2_besteller)}
                    </td>
                    <td
                      style={{ textAlign: 'right', padding: '6px 12px', color: COLOR_KQ2_ZAHLER }}
                    >
                      {fmtRate(c.kq2_zahler)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>

      {/* Streitfälle */}
      <Accordion
        expanded={streitOpen}
        onChange={(_, expanded) => setStreitOpen(expanded)}
        elevation={2}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h5">
            Streitfälle & unsichere Fälle
            {streit ? ` (${formatNumber(streit.totals.rows_total, 0)} Bestellungen)` : ''}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2 }}>
            Bestellungen, bei denen die Berechnungsarten widersprechen oder die nicht sauber
            bewertbar sind. Eine Bestellung kann mehrere Merkmale tragen. Spalten A / B / B+ =
            Meilenstein 1/2 nach Betrag, Laufzeit (streng), Laufzeit (anteilige zählen).
          </Typography>
          {streitLoading && <LoadingSpinner />}
          {!streitLoading && streit && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {flagChips.map((f) => (
                  <Chip
                    key={f.key}
                    label={`${f.label}: ${formatNumber(f.count, 0)}`}
                    sx={{ backgroundColor: colors.blueAccent[700] }}
                  />
                ))}
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {[
                        'Bestellung',
                        'Monat',
                        'Status',
                        'Zahlung',
                        'Fakturiert',
                        'Preis',
                        'Abo-Ende',
                        'A',
                        'B',
                        'B+',
                        'Merkmale',
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left',
                            padding: '6px 10px',
                            borderBottom: `2px solid ${colors.grey[700]}`,
                            color: colors.grey[100],
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {streit.data.map((s) => (
                      <tr key={s.ordernumber}>
                        <td style={{ padding: '4px 10px' }}>{s.ordernumber}</td>
                        <td style={{ padding: '4px 10px' }}>{s.month}</td>
                        <td style={{ padding: '4px 10px' }}>{s.vertrag_sts ?? '—'}</td>
                        <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                          {formatNumber(s.zahlung, 2)} €
                        </td>
                        <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                          {formatNumber(s.netto_faktura, 2)} €
                        </td>
                        <td style={{ padding: '4px 10px', textAlign: 'right' }}>
                          {s.price === null ? '—' : `${formatNumber(s.price, 2)} €`}
                        </td>
                        <td style={{ padding: '4px 10px' }}>{s.cap ?? '—'}</td>
                        <td style={{ padding: '4px 10px' }}>{fmtPaid(s.paid_a)}</td>
                        <td style={{ padding: '4px 10px' }}>{fmtPaid(s.paid_b_strict)}</td>
                        <td style={{ padding: '4px 10px' }}>{fmtPaid(s.paid_b_partial)}</td>
                        <td style={{ padding: '4px 10px' }}>
                          {Object.entries(s.flags)
                            .filter(([, v]) => v)
                            .map(([k]) => FLAG_LABELS[k])
                            .join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                <Button
                  size="small"
                  disabled={streitPage <= 1}
                  onClick={() => setStreitPage((p) => Math.max(1, p - 1))}
                >
                  Zurück
                </Button>
                <Typography variant="caption">
                  Seite {streit.pagination.page} / {streit.pagination.totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={streitPage >= streit.pagination.totalPages}
                  onClick={() => setStreitPage((p) => p + 1)}
                >
                  Weiter
                </Button>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default KuendigungsquotenPage;
